import { DeploymentDrawer } from "@/features/Deployments/components/DeploymentDrawer";
import { AppBanner } from "@/features/Notifications/AppBanner";
import { OnboardingOverlay } from "@/features/PromptbookOnboarding/components/OnboardingOverlay";
import { useViewResource } from "@/features/Resources/hooks/useViewResource";
import { useWorkspaceById } from "@/features/Workspaces/hooks/useWorkspaces";
import { Flex } from "@chakra-ui/react";
import { v2 } from "@pufflig/prompt-studio-types";
import { Page } from "@pufflig/prompt-studio-ui";
import { PromptbookRuntimeProvider } from "@pufflig/promptbook-runtime";
import { ErrorBoundary } from "@sentry/react";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { useRuntimeConfig } from "../../PromptbookRuntime/useRuntimeConfig";
import { usePromptbook } from "../../Promptbooks/hooks/usePromptbook";
import { PromptbookEditor } from "../components/PromptbookEditor";
import { PromptbookEditorHeader } from "../components/PromptbookEditorHeader";
import { PromptbookEditorPageSkeleton } from "../components/PromptbookEditorPageSkeleton";

type PromptbookEditorPageParams = {
  promptbookId: string;
};

export const PromptbookEditorPage = () => {
  const { promptbookId } = useParams() as PromptbookEditorPageParams;
  const { promptbook, isLoadingPromptbook, errorLoadingPromptbook } = usePromptbook(promptbookId);
  const [isDeploymentDrawerOpen, setDeploymentDrawerOpen] = useState(false);

  const workspaceId = promptbook?.workspace_id || "";
  const { data: workspace } = useWorkspaceById(workspaceId);
  const teamId = workspace?.teamId;

  const promptbookDefinition = promptbook?.editor_state as unknown as v2.BasePromptbookState;
  const resources = [
    ...Object.keys(promptbookDefinition?.resources || {}),
    ...Object.keys(promptbookDefinition?.results || {}),
  ];

  useViewResource(promptbook?.resource_id);

  const { config, isLoadingConfig } = useRuntimeConfig(promptbookId, resources);

  if (isLoadingPromptbook || isLoadingConfig) {
    return <PromptbookEditorPageSkeleton />;
  }

  if (errorLoadingPromptbook) {
    return <p>Something went wrong</p>;
  }

  return (
    <Page Banner={<AppBanner />}>
      <Flex width="100%" flex="1" direction="column" height="100%" overflow="hidden">
        <Helmet>
          <title>{promptbook?.name || "Promptbook"} - Prompt Studio</title>
        </Helmet>
        <PromptbookEditorHeader
          promptbookId={promptbookId}
          workspaceId={workspaceId}
          promptbookName={promptbook?.name || ""}
          onOpenDeploymentDrawer={() => setDeploymentDrawerOpen(true)}
        />
        <ErrorBoundary fallback={<p>Something went wrong</p>}>
          <PromptbookRuntimeProvider promptbookId={promptbookId} storageConfig={config.storage}>
            <PromptbookEditor promptbookId={promptbookId} teamId={teamId} promptbook={promptbookDefinition} />
          </PromptbookRuntimeProvider>
        </ErrorBoundary>
        <OnboardingOverlay />
        <DeploymentDrawer
          readOnly={false}
          promptbookId={promptbookId}
          workspaceId={workspaceId}
          isOpen={isDeploymentDrawerOpen}
          onClose={() => setDeploymentDrawerOpen(false)}
        />
      </Flex>
    </Page>
  );
};
